import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { OutcomeCaseStudyArticle } from "@/components/resources/OutcomeCaseStudyArticle";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import body from "../../content/resources/petwell-magento-shopify-plus-migration-case-study.en.md?raw";

const PATH = "/resources/petwell-magento-migration-case-study";
const ALT = "/es/recursos/caso-petwell-migracion-magento";
const TITLE = "Petwell: Migrating 10,000+ SKUs from Magento to Shopify Plus in 8 Weeks";
const META_TITLE = "Petwell Magento to Shopify Plus Case Study · Shugert";
const DESCRIPTION = "How Petwell moved 10,000+ SKUs from Magento to Shopify Plus in 8 weeks with zero downtime, historical data migration, taxonomy work and redirect mapping.";
const PUBLISHED = "2026-08-20T14:30:00.000Z";
const MODIFIED = "2026-08-20T14:30:00.000Z";

const SOURCES = [
  { label: "Shugert Marketing partner profile", publisher: "Shopify", href: "https://www.shopify.com/partners/directory/partner/shugert-marketing" },
  { label: "Petwell", publisher: "Petwell", href: "https://petwell.co.uk/" },
  { label: "Migrate to Shopify", publisher: "Shopify Help Center", href: "https://help.shopify.com/en/manual/migrating-to-shopify" },
  { label: "Site moves with URL changes", publisher: "Google Search Central", href: "https://developers.google.com/search/docs/crawling-indexing/site-move-with-url-changes" },
];

const RELATED = [
  { label: "Llantas Cavazos migration case study", href: "/resources/llantas-cavazos-migration-case-study" },
  { label: "Shopify migration service", href: "/shopify-migration" },
  { label: "Shopify Plus agency", href: "/shopify-plus-agency" },
  { label: "All Shopify case studies", href: "/work" },
];

export const Route = createFileRoute("/resources/petwell-magento-migration-case-study")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () => buildHead({
    locale: "en", title: META_TITLE, description: DESCRIPTION, path: PATH, altPath: ALT, type: "article",
    author: "Samuel Noriega", articleAuthor: "Samuel Noriega", articleSection: "Shopify migration case study", publishedTime: PUBLISHED, modifiedTime: MODIFIED,
    jsonLd: [
      articleJsonLdForPillar({ title: TITLE, description: DESCRIPTION, path: PATH, publishedTime: PUBLISHED, modifiedTime: MODIFIED, section: "Shopify migration case study" }),
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Work", path: "/work" }, { name: "Petwell case study", path: PATH }]),
    ],
  }),
  component: () => (
    <SiteShell>
      <OutcomeCaseStudyArticle
        locale="en" path={PATH} title={TITLE}
        excerpt="How a 10,000+ SKU Magento operation moved to Shopify Plus in eight weeks while preserving catalog integrity, historical customer data, URL intent, and continuous storefront availability."
        body={body} updated="August 2026" publishedAt={PUBLISHED} modifiedAt={MODIFIED} readingMinutes={10}
        focus="How Petwell treated catalog taxonomy, customer and order history, redirect mapping, QA, and cutover as one controlled Magento-to-Shopify Plus migration."
        audience="Established Magento merchants evaluating Shopify Plus where large catalog data, SEO continuity, and downtime risk are more important than reproducing the legacy platform screen for screen."
        sources={SOURCES} related={RELATED}
        category="Magento to Shopify Plus migration case study" backLabel="← Work" backHref="/work"
        cta={{ eyebrow: "Controlled replatform", title: "Move the business, not just the product export.", body: "We scope catalog integrity, historical data, redirects, integrations, QA, and cutover together so a replatform has explicit acceptance criteria before launch.", label: "Request a migration scope", href: "/get-a-quote?intent=rebuild&challenge=migrating&source=petwell-case-study" }}
      />
    </SiteShell>
  ),
});
